import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  Optional
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { finalize } from 'rxjs/operators';
import { CoreModule } from 'src/app/_theme/core/core.module';
import { CountryISO } from 'src/app/_theme/core/ao-intl-tel-input/enums/country-iso.enum';
import { SearchCountryField } from 'src/app/_theme/core/ao-intl-tel-input/enums/search-country-field.enum';
import { StatusPageService } from 'src/app/_theme/core/status-page/status-page.service';
import { STATUS_PAGE_SUBSCRIPTION_CHANNEL_OPTIONS } from '../shared/subscription-dialog.constants';
import {
  NotificationChannelId,
  NotificationChannelOption,
  PublicSubscribeRequest,
  ServiceSelectionGroup,
  ServiceSelectionOption,
  StatusMainPageSubscriptionDialogData,
  SubscribeDialogStep,
  SubmitNoticeType
} from '../shared/subscription-dialog.models';
import {
  getChannelLabel,
  resolveSubscriptionSubmitFailure
} from '../shared/subscription-dialog.submit-feedback';
import { SubscriptionServiceSelectionState } from '../shared/subscription-dialog.service-selection';

function strictEmailValidator(control: AbstractControl): ValidationErrors | null {
  const value = String(control.value ?? '').trim();
  if (!value) {
    return null;
  }

  const parts = value.split('@');
  if (parts.length !== 2) {
    return { email: true };
  }

  const [localPart, domainPart] = parts;
  const localPartPattern = /^[A-Za-z0-9_%+-]+(?:\.[A-Za-z0-9_%+-]+)*$/;
  const domainPattern = /^(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
  return localPartPattern.test(localPart) && domainPattern.test(domainPart) ? null : { email: true };
}

function webhookUrlValidator(control: AbstractControl): ValidationErrors | null {
  const value = String(control.value ?? '').trim();
  if (!value) {
    return null;
  }

  if (/[\s<>]/.test(value)) {
    return { invalidWebhookUrl: true };
  }

  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return { invalidWebhookUrl: true };
    }
    return url.hostname ? null : { invalidWebhookUrl: true };
  } catch {
    return { invalidWebhookUrl: true };
  }
}

@Component({
  selector: 'app-status-main-page-public-subscription-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    CoreModule
  ],
  templateUrl: './public-subscription-dialog.component.html',
  styleUrls: [
    '../shared/styles/subscription-dialog.component.scss',
    '../shared/styles/subscription-dialog.component.services-verification.scss',
    '../shared/styles/subscription-dialog.component.theme-panel-overrides.scss',
    '../shared/styles/subscription-dialog.component.theme-panel-overrides.dark.scss',
    '../shared/styles/subscription-dialog.component.theme-panel-overrides.public-dark.scss',
    '../shared/styles/subscription-dialog.component.theme-panel-overrides.light.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StatusMainPagePublicSubscriptionDialogComponent implements OnInit {
  activeTabIndex = 0;
  form: FormGroup;
  currentStep: SubscribeDialogStep = 'channel';
  submittedAddress = '';
  submittedChannelId: NotificationChannelId = 'email';
  servicesLoading = false;
  isSubmitting = false;
  submitError = '';
  submitNoticeType: SubmitNoticeType = 'error';
  availableChannels: NotificationChannelOption[] = [];
  readonly CountryISO = CountryISO;
  readonly SearchCountryField = SearchCountryField;
  readonly preferredCountries: CountryISO[] = [CountryISO.UnitedStates, CountryISO.India];
  readonly serviceSelection = new SubscriptionServiceSelectionState();
  private readonly channelOptions = STATUS_PAGE_SUBSCRIPTION_CHANNEL_OPTIONS;

  constructor(
    private readonly dialogRef: MatDialogRef<StatusMainPagePublicSubscriptionDialogComponent>,
    private readonly fb: FormBuilder,
    private readonly statusPageService: StatusPageService,
    private readonly _cd: ChangeDetectorRef,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: StatusMainPageSubscriptionDialogData
  ) {
    this.form = this.fb.group({
      email: ['', [strictEmailValidator]],
      slack: [''],
      sms: [null],
      msTeams: ['', [webhookUrlValidator]],
      webhook: ['', [webhookUrlValidator]]
    });
  }

  ngOnInit(): void {
    if (this.data?.mode === 'completed' || this.data?.mode === 'unsubscribed') {
      this.currentStep = this.data.mode;
      return;
    }

    this.setupEnabledChannels();
    this.currentStep = 'channel';
    this.loadServiceOptions();
  }

  close(): void {
    this.dialogRef.close();
  }

  get subscribeAllServices(): boolean {
    return this.serviceSelection.subscribeAllServices;
  }

  get isAllServicesSelected(): boolean {
    return this.serviceSelection.isAllServicesSelected();
  }

  get serviceTreeTouched(): boolean {
    return this.serviceSelection.serviceTreeTouched;
  }

  get selectedServiceIds(): number[] {
    return this.serviceSelection.selectedServiceIds;
  }

  get serviceGroups(): ServiceSelectionGroup[] {
    return this.serviceSelection.serviceGroups;
  }

  get filteredServiceGroups(): ServiceSelectionGroup[] {
    return this.serviceSelection.filteredServiceGroups;
  }

  get ungroupedServiceOptions(): ServiceSelectionOption[] {
    return this.serviceSelection.ungroupedServiceOptions;
  }

  get filteredUngroupedServiceOptions(): ServiceSelectionOption[] {
    return this.serviceSelection.filteredUngroupedServiceOptions;
  }

  get serviceSearchQuery(): string {
    return this.serviceSelection.serviceSearchQuery;
  }

  get hasActiveServiceSearch(): boolean {
    return this.serviceSelection.hasActiveServiceSearch;
  }

  get hasServiceSearchResults(): boolean {
    return this.serviceSelection.hasServiceSearchResults;
  }

  get serviceSelectionActionLabel(): string {
    return this.isAllServicesSelected ? 'Clear All' : 'Select All';
  }

  get isActiveValid(): boolean {
    const control = this.getActiveControl();
    if (!control) {
      return false;
    }

    if (this.getActiveChannelId() === 'sms') {
      return !!control.value && control.valid;
    }

    const value = (control.value ?? '').toString().trim();
    if (!value.length || control.invalid) {
      return false;
    }

    return true;
  }

  get showCloseOnlyAfterDuplicate(): boolean {
    if (!this.submitError || this.submitNoticeType !== 'info') {
      return false;
    }
    return this.submittedChannelId === 'email' || this.submittedChannelId === 'sms';
  }

  get isLimitReached(): boolean {
    const max = this.data?.maxSubscribers;
    if (max === null || max === undefined) {
      return false;
    }
    const currentRaw = this.data?.currentSubscribers;
    const current = Number.isFinite(Number(currentRaw)) ? Number(currentRaw) : 0;
    return current >= max;
  }

  get limitMessage(): string {
    const max = this.data?.maxSubscribers;
    if (max === null || max === undefined) {
      return '';
    }
    const currentRaw = this.data?.currentSubscribers;
    const current = Number.isFinite(Number(currentRaw)) ? Number(currentRaw) : null;
    if (current === null) {
      return `Maximum subscribers reached (${max}).`;
    }
    return `Maximum subscribers reached (${current}/${max}).`;
  }

  get verificationIcon(): string {
    switch (this.submittedChannelId) {
      case 'sms':
        return 'sms';
      case 'webhook':
        return 'link';
      case 'slack':
        return 'tag';
      case 'msTeams':
        return 'groups';
      case 'email':
      default:
        return 'mail_outline';
    }
  }

  get verificationTitle(): string {
    switch (this.submittedChannelId) {
      case 'sms':
        return 'Check your messages';
      case 'webhook':
        return 'Check your webhook endpoint';
      case 'slack':
        return 'Check Slack';
      case 'msTeams':
        return 'Check your Microsoft Teams webhook';
      case 'email':
      default:
        return 'Check your email';
    }
  }

  get verificationIntro(): string {
    const address = String(this.submittedAddress || '').trim();
    switch (this.submittedChannelId) {
      case 'sms':
        return address
          ? `We sent a verification message to ${address}.`
          : 'We sent a verification message to your phone number.';
      case 'webhook':
        return address
          ? `We sent a verification request to ${address}.`
          : 'We sent a verification request to your webhook URL.';
      case 'slack':
        return address
          ? `We sent a verification message to ${address}.`
          : 'We sent a verification message to your Slack channel.';
      case 'msTeams':
        return address
          ? `We sent a verification request to ${address}.`
          : 'We sent a verification request to your Microsoft Teams webhook URL.';
      case 'email':
      default:
        return address
          ? `We sent a verification link to ${address}.`
          : 'We sent a verification link to your email address.';
    }
  }

  get verificationSubtext(): string {
    switch (this.submittedChannelId) {
      case 'sms':
        return 'Open the message and follow the verification instructions to finish subscribing. You can close this window.';
      case 'webhook':
        return 'Confirm your endpoint received the verification request to finish subscribing. You can close this window.';
      case 'slack':
        return 'Open Slack and follow the verification instructions to finish subscribing. You can close this window.';
      case 'msTeams':
        return 'Confirm Microsoft Teams received the verification request to finish subscribing. You can close this window.';
      case 'email':
      default:
        return 'Open the email and click the verification link to finish subscribing. You can close this window.';
    }
  }

  onContinue(): void {
    if (this.isLimitReached) {
      return;
    }

    this.normalizeActiveChannelInput();
    const control = this.getActiveControl();
    control?.markAsTouched();
    if (this.isActiveValid) {
      this.currentStep = 'service-selection';
      this.clearSubmitFeedback();
      this._cd.markForCheck();
    }
  }

  onBackToChannels(): void {
    this.currentStep = 'channel';
    this.clearSubmitFeedback();
  }

  onSubscribe(): void {
    if (this.isSubmitting || this.isLimitReached) {
      return;
    }
    if (!this.subscribeAllServices && this.selectedServiceIds.length === 0) {
      this.serviceSelection.serviceTreeTouched = true;
      this.clearSubmitFeedback();
      this._cd.markForCheck();
      return;
    }

    const org = String(this.data?.org || '').trim();
    const slug = String(this.data?.slug || '').trim();
    if (!slug || !org) {
      this.submitError = 'Missing page context. Please refresh and try again.';
      return;
    }
    if (!this.validatePublicChannelBeforeSubmit()) {
      return;
    }

    const activeChannel = this.availableChannels[this.activeTabIndex];
    this.submittedChannelId = activeChannel?.id || 'email';

    const address = this.resolvePublicAddressForPayload();
    const payload: PublicSubscribeRequest = {
      address,
      subscriberType: this.getSubscriberType(),
      subscribeToAllServices: this.subscribeAllServices,
      serviceIds: this.subscribeAllServices ? [] : this.selectedServiceIds,
      notificationPreferences: ''
    };

    this.clearSubmitFeedback();
    this.submittedAddress = address;
    this.isSubmitting = true;

    this.statusPageService
      .subscribePublicStatusPage(org, slug, payload, String(this.data?.ssoToken || '').trim())
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this._cd.markForCheck();
        })
      )
      .subscribe({
        next: (response: any) => {
          const isSuccess = response?.success !== false;
          if (!isSuccess) {
            this.applySubmitFailure(response);
            return;
          }
          this.currentStep = 'verifying';
        },
        error: (error) => {
          this.applySubmitFailure(error);
        }
      });
  }

  onToggleAllServices(value: boolean): void {
    this.serviceSelection.toggleAllServices(value);
    if (value) {
      this.clearSubmitFeedback();
      this._cd.markForCheck();
    }
  }

  onToggleAllServicesAction(): void {
    this.onToggleAllServices(!this.isAllServicesSelected);
  }

  onServiceSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.serviceSelection.setServiceSearchQuery(input?.value || '');
    this._cd.markForCheck();
  }

  clearServiceSearch(): void {
    this.serviceSelection.clearServiceSearch();
    this._cd.markForCheck();
  }

  onChannelSelected(index: number): void {
    if (this.activeTabIndex === index) {
      return;
    }
    this.activeTabIndex = index;
    this.clearSubmitFeedback();
  }

  trackByChannelId(index: number, channel: NotificationChannelOption): string | number {
    return channel?.id || index;
  }

  trackByServiceSelectionId(index: number, service: ServiceSelectionOption): number {
    return service?.serviceId || index;
  }

  trackByServiceGroupId(index: number, group: ServiceSelectionGroup): string | number {
    return group?.groupId ?? group?.name ?? index;
  }

  getActiveControl() {
    const channel = this.availableChannels[this.activeTabIndex];
    if (!channel) {
      return null;
    }
    return this.form.get(channel.formControlName);
  }

  toggleService(service: ServiceSelectionOption, checked: boolean): void {
    this.serviceSelection.toggleService(service, checked);
    if (this.selectedServiceIds.length > 0) {
      this.submitError = '';
    }
    this._cd.markForCheck();
  }

  toggleServiceGroup(group: ServiceSelectionGroup, checked: boolean): void {
    this.serviceSelection.toggleServiceGroup(group, checked);
    if (this.selectedServiceIds.length > 0) {
      this.submitError = '';
    }
    this._cd.markForCheck();
  }

  toggleGroupExpansion(group: ServiceSelectionGroup): void {
    this.serviceSelection.toggleGroupExpansion(group);
    this._cd.markForCheck();
  }

  isServiceGroupSelected(group: ServiceSelectionGroup): boolean {
    return this.serviceSelection.isServiceGroupSelected(group);
  }

  isServiceGroupIndeterminate(group: ServiceSelectionGroup): boolean {
    return this.serviceSelection.isServiceGroupIndeterminate(group);
  }

  isServiceSelected(service: ServiceSelectionOption): boolean {
    return this.serviceSelection.isServiceSelected(service);
  }

  private getActiveChannelId(): NotificationChannelId | '' {
    const channel = this.availableChannels[this.activeTabIndex];
    return channel?.id || '';
  }

  private getSubscriberType(): number {
    const channel = this.availableChannels[this.activeTabIndex];
    return channel?.subscriberType || 1;
  }

  private validatePublicChannelBeforeSubmit(): boolean {
    this.normalizeActiveChannelInput();
    const control = this.getActiveControl();
    control?.markAsTouched();

    if (this.isActiveValid) {
      return true;
    }

    this.currentStep = 'channel';
    this._cd.markForCheck();
    return false;
  }

  private resolvePublicAddressForPayload(): string {
    const activeChannelId = this.getActiveChannelId();
    const activeControlValue = this.getActiveControl()?.value;

    if (activeChannelId === 'sms') {
      return this.resolveSmsAddress(activeControlValue);
    }

    const controlValue = String(activeControlValue || '').trim();
    if (activeChannelId === 'email') {
      return controlValue.toLowerCase();
    }

    return controlValue;
  }

  private normalizeActiveChannelInput(): void {
    const activeChannelId = this.getActiveChannelId();
    const control = this.getActiveControl();
    if (!control) {
      return;
    }

    if (activeChannelId === 'sms') {
      return;
    }

    const raw = String(control.value ?? '');
    let normalized = raw.trim();
    if (activeChannelId === 'email') {
      normalized = normalized.toLowerCase();
    }

    if (normalized !== raw) {
      control.setValue(normalized);
    }
  }

  private extractDigits(value: any): string {
    return String(value ?? '').replaceAll(/\D+/g, '');
  }

  private resolveSmsAddress(value: any): string {
    const e164Number = String(value?.e164Number || '').trim();
    if (e164Number) {
      return e164Number;
    }

    const dialCode = String(value?.dialCode || '').trim();
    const localNumber = this.extractDigits(value?.number);
    if (!dialCode || !localNumber) {
      return localNumber;
    }
    return `${dialCode}${localNumber}`;
  }

  private loadServiceOptions(): void {
    this.servicesLoading = true;
    this.serviceSelection.initialize(
      this.data?.availableServices || [],
      this.data?.availableServiceGroups || []
    );
    this.servicesLoading = false;
    this._cd.markForCheck();
  }

  private setupEnabledChannels(): void {
    const normalize = (value: any): string =>
      String(value ?? '')
        .trim()
        .toLowerCase()
        .replaceAll(/[\s_-]+/g, '');

    const enabledList = (this.data?.enabledChannels || [])
      .map((channel) => normalize(channel))
      .filter((value) => !!value);
    const enabled = new Set(enabledList);

    if (!enabled.size) {
      this.availableChannels = [...this.channelOptions];
      this.activeTabIndex = 0;
      return;
    }

    const has = (key: string): boolean => enabled.has(key);
    const isEmail = enabled.has('email') || enabled.has('emailofficial');
    const isSms =
      enabled.has('sms') ||
      enabled.has('text') ||
      enabled.has('textmessage') ||
      enabled.has('smsofficial');
    const isSlack = has('slack');
    const isWebhook = has('webhook');
    const isTeams = has('msteams') || has('microsoftteams') || has('teams');

    this.availableChannels = this.channelOptions.filter((channel) => {
      switch (channel.id) {
        case 'email':
          return isEmail;
        case 'sms':
          return isSms;
        case 'slack':
          return isSlack;
        case 'webhook':
          return isWebhook;
        case 'msTeams':
          return isTeams;
        default:
          return false;
      }
    });

    if (!this.availableChannels.length) {
      this.availableChannels = this.channelOptions.filter((channel) => channel.id === 'email');
    }
    this.activeTabIndex = 0;
  }

  private clearSubmitFeedback(): void {
    this.submitError = '';
    this.submitNoticeType = 'error';
  }

  private applySubmitFailure(errorOrMessage: any): void {
    const resolved = resolveSubscriptionSubmitFailure(
      errorOrMessage,
      this.getChannelLabel(this.submittedChannelId)
    );
    this.submitError = resolved.message;
    this.submitNoticeType = resolved.type;
    this.currentStep = 'service-selection';
  }

  private getChannelLabel(channelId: NotificationChannelId): string {
    return getChannelLabel(this.channelOptions, channelId);
  }
}
